import { formatDateToString } from '@convenia/helpers'
import { SYSTEM_FIELD_TYPE_DATE } from '@convenia/common-organisms/SystemFields/content/consts/FieldTypes'

/**
 * Formats one custom field value before it is sent to the API.
 */
const formatFieldValue = (type, value) => {
  if (type === SYSTEM_FIELD_TYPE_DATE) {
    return value ? formatDateToString(value, 'DD/MM/YYYY') : null
  }

  return value ?? null
}

/**
 * Extracts custom field values from params based on the given schema.
 *
 * @param {Object} params
 * @param {Object} params.schema
 * @param {string} params.id
 * @returns {Object} Custom field values
 */
export const getCustomFieldValues = (params) => {
  const { schema, ...data } = params || {}

  const keys = Object.keys(schema || {})

  const customFields = keys.reduce((acc, key) => {
    acc[key] = data[key] || null
    return acc
  }, {})

  return customFields
}

/**
 * Maps custom field values to the payload expected by the API.
 *
 * @param {Object} params
 * @param {string} params.sectionId
 * @param {string?} params.entityId (optional)
 * @param {Object} params.data
 * @param {Object} params.schema
 * @returns {Array} Mapped custom field values
 */
export const mapCustomFieldValues = ({ sectionId, entityId, schema = {}, data = {} } = {}) => ({
  section_id: sectionId || '',
  fields: Object.entries(schema)
    .filter(([ , field ]) => !field?.disabled)
    .map(([ custom_field_id, field ]) => ({
      custom_field_id,
      entity_id: entityId,
      value: formatFieldValue(field?.type, data[custom_field_id])
    }))
})

/**
 * Merges custom field values into items by matching either item ID or index.
 *
 * @param {Array<Object>} items - Array of items to merge values into
 * @param {Object} values - Object containing values to merge, keyed by item ID or index
 * @returns {Array<Object>} Array of items with merged custom field values
 */
export const mergeCustomFieldValues = (items = [], values = {}) =>
  items.map((item, index) => ({
    ...item,
    ...(values?.[item.id] || values?.[index] || {})
  }))
